/**
 * Parse vendor description HTML to extract image and clean text separately
 * @param {string} htmlDescription - Raw HTML description from vendor
 * @returns {{image: string|null, description: string}} - Extracted image URL and plain text description (HTML stripped)
 */
export function parseVendorDescription(htmlDescription) {
  if (!htmlDescription) {
    return { image: null, description: '' };
  }

  // Create a temporary DOM element to parse HTML
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = htmlDescription;

  // Find the first image
  const imgElement = tempDiv.querySelector('img');
  const imageUrl = imgElement ? imgElement.src : null;

  // Remove all images from the description
  const images = tempDiv.querySelectorAll('img');
  images.forEach(img => img.remove());

  // Get plain text (strips all HTML tags and decodes entities)
  const cleanText = tempDiv.textContent || tempDiv.innerText || '';

  return {
    image: imageUrl,
    description: cleanText?.trim(),
  };
}

/**
 * Generate JSON-LD structured data for vendor profile
 * @param {Object} vendorData - Vendor information
 * @param {string} vendorData.name - Vendor name
 * @param {string} vendorData.description - Vendor description (text only)
 * @param {string} vendorData.image - Vendor image URL
 * @param {string} vendorData.url - Vendor page URL
 * @returns {string} - JSON-LD structured data as string
 */
export function generateVendorJsonLd(vendorData) {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ProfilePage',
    mainEntity: {
      '@type': 'Organization',
      name: vendorData.name,
      description: vendorData.description,
      url: vendorData.url,
    },
  };

  // Only add image if it exists
  if (vendorData.image) {
    jsonLd.mainEntity.logo = {
      '@type': 'ImageObject',
      url: vendorData.image,
    };
  }

  return JSON.stringify(jsonLd);
}
